import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'components/ui/Modal'
import { Button } from 'components/ui/Button'
import { CheckCircle, XCircle, Video, Mic, AlertTriangle } from 'lucide-react'

const DeviceCameraPreview = React.memo(function DeviceCameraPreview({
    videoRef,
}: {
    videoRef: React.RefObject<HTMLVideoElement | null>
}) {
    return (
        <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className="w-full h-full object-cover transform scale-x-[-1]"
        />
    )
})

interface DeviceTestModalProps {
    isOpen: boolean
    onClose: () => void
    onTestComplete: () => void
}

export function DeviceTestModal({ isOpen, onClose, onTestComplete }: DeviceTestModalProps) {
    const { t } = useTranslation()
    const [cameraStatus, setCameraStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle')
    const [microphoneStatus, setMicrophoneStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle')
    const [errorMessage, setErrorMessage] = useState('')

    const videoRef = useRef<HTMLVideoElement>(null)
    const streamRef = useRef<MediaStream | null>(null)
    const audioLevelRef = useRef<HTMLDivElement>(null)
    const audioContextRef = useRef<AudioContext | null>(null)
    const analyserRef = useRef<AnalyserNode | null>(null)
    const animationFrameRef = useRef<number | null>(null)
    const monitoringRef = useRef(false)

    // Start device test
    const startTest = async () => {
        setCameraStatus('testing')
        setMicrophoneStatus('testing')
        setErrorMessage('')

        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({
                audio: true,
                video: true
            })

            streamRef.current = mediaStream

            // Setup video
            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream
                videoRef.current.play()
            }

            // Setup audio analysis
            const audioContext = new AudioContext()
            const analyser = audioContext.createAnalyser()
            const source = audioContext.createMediaStreamSource(mediaStream)

            analyser.fftSize = 256
            source.connect(analyser)

            audioContextRef.current = audioContext
            analyserRef.current = analyser

            // Start monitoring audio level
            monitorAudioLevel()

            setCameraStatus('success')
            setMicrophoneStatus('success')

        } catch (err: any) {
            console.error('Device test failed:', err)
            setCameraStatus('error')
            setMicrophoneStatus('error')

            if (err.name === 'NotAllowedError') {
                setErrorMessage(t('interview.deviceTest.permissionDenied'))
            } else if (err.name === 'NotFoundError') {
                setErrorMessage(t('interview.deviceTest.deviceNotFound'))
            } else {
                setErrorMessage(t('interview.deviceTest.genericError'))
            }
        }
    }

    const stopMonitoring = () => {
        monitoringRef.current = false
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current)
            animationFrameRef.current = null
        }
    }

    // Update the meter directly so the playing <video> is not reconciled on every frame.
    const monitorAudioLevel = () => {
        if (!analyserRef.current) return

        const analyser = analyserRef.current
        const dataArray = new Uint8Array(analyser.frequencyBinCount)
        monitoringRef.current = true

        const checkLevel = () => {
            if (!monitoringRef.current || !analyserRef.current) return
            analyser.getByteFrequencyData(dataArray)
            const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length
            const normalizedLevel = Math.min(100, (average / 128) * 100)
            if (audioLevelRef.current) {
                audioLevelRef.current.style.width = `${normalizedLevel}%`
            }

            animationFrameRef.current = requestAnimationFrame(checkLevel)
        }

        checkLevel()
    }

    // Cleanup on unmount or close. Refs stay current for the mount-only effect below.
    const cleanup = () => {
        stopMonitoring()

        const mediaStream = streamRef.current
        if (mediaStream) {
            mediaStream.getTracks().forEach(track => track.stop())
            streamRef.current = null
        }

        if (audioContextRef.current) {
            audioContextRef.current.close().catch(() => {})
            audioContextRef.current = null
        }
        analyserRef.current = null

        if (videoRef.current) {
            videoRef.current.srcObject = null
        }
        if (audioLevelRef.current) {
            audioLevelRef.current.style.width = '0%'
        }
    }

    useEffect(() => {
        return cleanup
    }, [])

    // Handle test completion
    const handleComplete = () => {
        cleanup()
        onTestComplete()
    }

    // Handle retry
    const handleRetry = () => {
        cleanup()
        setCameraStatus('idle')
        setMicrophoneStatus('idle')
        setErrorMessage('')
        startTest()
    }

    const isTestSuccessful = cameraStatus === 'success' && microphoneStatus === 'success'
    const isTestFailed = cameraStatus === 'error' || microphoneStatus === 'error'

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t('interview.deviceTest.title')}
            hideCloseButton={true}
            size="lg"
            footer={
                <div className="flex justify-end gap-3">
                    {cameraStatus === 'idle' && (
                        <Button onClick={startTest}>
                            {t('interview.deviceTest.startTest')}
                        </Button>
                    )}

                    {isTestFailed && (
                        <Button onClick={handleRetry} variant="outline">
                            {t('interview.deviceTest.retry')}
                        </Button>
                    )}

                    {isTestSuccessful && (
                        <Button onClick={handleComplete}>
                            {t('interview.deviceTest.continue')}
                        </Button>
                    )}
                </div>
            }
        >
            <div className="space-y-6">
                {/* Instructions */}
                {cameraStatus === 'idle' && (
                    <div className="bg-muted/30 p-4 rounded-lg">
                        <p className="text-sm text-muted-foreground">
                            {t('interview.deviceTest.instructions')}
                        </p>
                    </div>
                )}

                {/* Error message */}
                {errorMessage && (
                    <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <p className="text-sm text-destructive font-medium">
                                {errorMessage}
                            </p>
                        </div>
                    </div>
                )}

                {/* Device status */}
                <div className="grid grid-cols-2 gap-4">
                    {/* Camera status */}
                    <div className="bg-card border rounded-lg p-4">
                        <div className="flex items-center gap-3 mb-2">
                            <Video className="h-5 w-5 text-muted-foreground" />
                            <span className="font-medium">{t('interview.deviceTest.camera')}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            {cameraStatus === 'idle' && (
                                <span className="text-sm text-muted-foreground">
                                    {t('interview.deviceTest.notTested')}
                                </span>
                            )}
                            {cameraStatus === 'testing' && (
                                <span className="text-sm text-muted-foreground">
                                    {t('interview.deviceTest.testing')}...
                                </span>
                            )}
                            {cameraStatus === 'success' && (
                                <>
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                    <span className="text-sm text-green-600">
                                        {t('interview.deviceTest.working')}
                                    </span>
                                </>
                            )}
                            {cameraStatus === 'error' && (
                                <>
                                    <XCircle className="h-4 w-4 text-destructive" />
                                    <span className="text-sm text-destructive">
                                        {t('interview.deviceTest.failed')}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Microphone status */}
                    <div className="bg-card border rounded-lg p-4">
                        <div className="flex items-center gap-3 mb-2">
                            <Mic className="h-5 w-5 text-muted-foreground" />
                            <span className="font-medium">{t('interview.deviceTest.microphone')}</span>
                        </div>
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                {microphoneStatus === 'idle' && (
                                    <span className="text-sm text-muted-foreground">
                                        {t('interview.deviceTest.notTested')}
                                    </span>
                                )}
                                {microphoneStatus === 'testing' && (
                                    <span className="text-sm text-muted-foreground">
                                        {t('interview.deviceTest.testing')}...
                                    </span>
                                )}
                                {microphoneStatus === 'success' && (
                                    <>
                                        <CheckCircle className="h-4 w-4 text-green-600" />
                                        <span className="text-sm text-green-600">
                                            {t('interview.deviceTest.working')}
                                        </span>
                                    </>
                                )}
                                {microphoneStatus === 'error' && (
                                    <>
                                        <XCircle className="h-4 w-4 text-destructive" />
                                        <span className="text-sm text-destructive">
                                            {t('interview.deviceTest.failed')}
                                        </span>
                                    </>
                                )}
                            </div>

                            {/* Audio level indicator */}
                            {microphoneStatus === 'success' && (
                                <div className="space-y-1">
                                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                                        <div
                                            ref={audioLevelRef}
                                            className="h-full bg-green-600 transition-all duration-100"
                                            style={{ width: '0%' }}
                                        />
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        {t('interview.deviceTest.speakToTest')}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {/* Video preview */}
                <div className="bg-black rounded-lg overflow-hidden aspect-video relative">
                    <DeviceCameraPreview videoRef={videoRef} />

                    {cameraStatus === 'idle' && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                            <div className="text-white text-center">
                                <Video className="h-12 w-12 mx-auto mb-2 opacity-50" />
                                <p className="text-sm">{t('interview.deviceTest.cameraPreview')}</p>
                            </div>
                        </div>
                    )}
                </div>

            </div>
        </Modal>
    )
}
